import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QuotationDetailDto } from '../lib/quotationApi';
import { quotationEmailDraft } from '../features/quotations/quotationEmailDraft';
import { formatDateTime as fmtDateTime } from '../utils/dateFormat';
import EmailDraftFields from './EmailDraftFields';

interface SendQuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: QuotationDetailDto;
  /**
   * The vessel the draft names: the page's answer (D104.12 - the quotation's own copy, else the RFQ's),
   * the one its header shows. Null when nothing names one: the draft says none, never a placeholder.
   */
  vesselName: string | null;
  /**
   * Sends the quotation — this form is the last step, SEND is the send. The
   * button stays disabled until the returned promise settles, so one click is
   * one email. The caller closes the modal on success and reports a failure
   * itself; on a failure the form stays open and filled in for another try,
   * unless the mail went (D180, `emailAlreadyWent`), when the caller closes it.
   */
  onSend: (emailData: SendQuotationData) => Promise<void>;
}

export interface SendQuotationData {
  /** All email addresses the quotation is sent to (e.g. captain, purchasing officer, agent). */
  recipients: string[];
  subject: string;
  /** What the Email text box holds, sent as is (D180). */
  body: string;
  /** Only when the person saw the already-sent warning. */
  resend?: true;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendQuotationModal: React.FC<SendQuotationModalProps> = ({
  isOpen,
  onClose,
  quotation,
  vesselName,
  onSend
}) => {
  const [formData, setFormData] = useState<SendQuotationData>({ recipients: [], subject: '', body: '' });
  const [recipientInput, setRecipientInput] = useState('');
  const [sending, setSending] = useState(false);
  // D105.2: the form's own input checks show beside the field, never in a browser alert.
  const [recipientError, setRecipientError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const sentAt = quotation.emailMetadata?.sentAt;

  // The page mounts this once, so the draft is built on each open, from the vessel of that moment.
  useEffect(() => {
    if (!isOpen) return;
    const draft = quotationEmailDraft({
      customerName: quotation.customer?.customerName,
      quotationNumber: quotation.quotationNumber,
      vesselName,
    });
    setFormData((prev) => ({ ...prev, ...draft }));
    setSubjectError('');
    setBodyError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /** Add the typed address(es) — comma/semicolon/space separated — to the recipient list. */
  const addRecipients = (): boolean => {
    const parts = recipientInput
      .split(/[,;\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return true;
    const invalid = parts.filter((p) => !EMAIL_RE.test(p));
    if (invalid.length) {
      setRecipientError(`Invalid email address: ${invalid.join(', ')}`);
      return false;
    }
    setRecipientError('');
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.concat(parts.filter((p) => !prev.recipients.includes(p)))
    }));
    setRecipientInput('');
    return true;
  };

  const removeRecipient = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((r) => r !== email)
    }));
  };

  const handleSubmit = async () => {
    // Fold any address still sitting in the input into the list first
    const pending = recipientInput.trim();
    let recipients = formData.recipients;
    if (pending) {
      const parts = pending.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
      const invalid = parts.filter((p) => !EMAIL_RE.test(p));
      if (invalid.length) {
        setRecipientError(`Invalid email address: ${invalid.join(', ')}`);
        return;
      }
      recipients = recipients.concat(parts.filter((p) => !recipients.includes(p)));
    }
    if (recipients.length === 0) {
      setRecipientError('Please add at least one recipient email address');
      return;
    }
    setRecipientError('');
    if (!formData.subject.trim()) {
      setSubjectError('Please enter a subject');
      return;
    }
    setSubjectError('');
    if (!formData.body.trim()) {
      setBodyError('Please enter the email text');
      return;
    }
    setBodyError('');

    try {
      setSending(true);
      await onSend({ ...formData, recipients, ...(sentAt ? { resend: true as const } : {}) });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 modal-overlay-safe">
      {/* Modal Container */}
      <div 
        className="bg-white rounded-lg relative w-full max-w-[610px] max-h-[90vh] overflow-y-auto flex flex-col"
        style={{
          minHeight: '600px'
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4">
          <h2 className="text-base font-semibold leading-6 text-slate-900">
            Send Quotation
          </h2>
          
          {/* Close Button: not while sending, the send's answer has to land on an open form */}
          <button
            onClick={onClose}
            disabled={sending}
            className="w-6 h-6 flex items-center justify-center bg-[#D9D9D9] rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span className="text-slate-400 text-lg leading-none">×</span>
          </button>
        </div>

        {/* Form Fields */}
        <div className="flex-1 px-6 pb-4">
          {sentAt && (
            <p className="mb-4 text-xs text-slate-500" style={{ fontFamily: 'Public Sans' }}>
              {`Sent on ${fmtDateTime(sentAt)} — sending again will resend.`}
            </p>
          )}

          {/* Recipients */}
          <div className="mb-4">
            <label
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Recipients <span className="text-red-500">*</span>
            </label>
            {formData.recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {formData.recipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm"
                    style={{ fontFamily: 'Public Sans' }}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      className="text-blue-500 hover:text-blue-800 leading-none"
                      aria-label={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={recipientInput}
                onChange={(e) => {
                  setRecipientInput(e.target.value);
                  setRecipientError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addRecipients();
                  }
                }}
                onBlur={() => addRecipients()}
                placeholder="captain@vessel.com, purchasing@agent.com"
                className="flex-1 px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
                style={{
                  fontFamily: 'Public Sans',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              />
              <button
                type="button"
                onClick={addRecipients}
                className="px-4 py-2 border border-primary-500 text-primary-500 rounded-md text-sm font-semibold hover:bg-blue-50"
                style={{ fontFamily: 'Public Sans' }}
              >
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500" style={{ fontFamily: 'Public Sans' }}>
              Add multiple addresses (e.g. captain, purchasing officer, agent) — separate with commas or press Enter.
            </p>
            {recipientError && (
              <p role="alert" className="mt-1 text-xs text-red-600" style={{ fontFamily: 'Public Sans' }}>
                {recipientError}
              </p>
            )}
          </div>

          <EmailDraftFields
            subject={formData.subject}
            onSubjectChange={(subject) => {
              setFormData((prev) => ({ ...prev, subject }));
              setSubjectError('');
            }}
            body={formData.body}
            onBodyChange={(body) => {
              setFormData((prev) => ({ ...prev, body }));
              setBodyError('');
            }}
            attachment={`Quotation ${quotation.quotationNumber} (Excel)`}
            subjectError={subjectError}
            bodyError={bodyError}
            disabled={sending}
          />
        </div>

        {/* Send Button */}
        <div className="p-6 pt-0">
          <button
            onClick={handleSubmit}
            disabled={sending}
            className="w-full bg-primary-500 rounded-lg flex items-center justify-center hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors h-10"
          >
          <span 
            className="text-white"
            style={{
              fontFamily: 'Public Sans',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px'
            }}
          >
            {sending ? 'Sending...' : sentAt ? 'Resend' : 'Send'}
          </span>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default SendQuotationModal;
